import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { execute } from '@/graphql/execute';
import { cn } from '@/lib/utils';
import { inviteOnboardingUsersMutation } from '@/queries/organization.query';
import { Plus, X } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

interface Invitee {
  firstName: string;
  email: string;
}

const emptyInvitee = (): Invitee => ({ firstName: '', email: '' });

export const InviteTeamForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);

  const [invitees, setInvitees] = useState<Invitee[]>([emptyInvitee()]);
  const [message, setMessage] = useState('');

  const updateInvitee = (index: number, field: keyof Invitee, value: string) => {
    setInvitees((prev) => prev.map((inv, i) => (i === index ? { ...inv, [field]: value } : inv)));
  };

  const addRow = () => {
    setInvitees((prev) => [...prev, emptyInvitee()]);
  };

  const removeRow = (index: number) => {
    if (invitees.length <= 1) return;
    setInvitees((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const validInvitees = invitees.filter((inv) => inv.firstName.trim() && inv.email.trim());

    if (validInvitees.length === 0) {
      toast.error(t('onboarding.inviteTeam.addAtLeastOne'));
      return;
    }

    setIsLoading(true);
    const toastId = toast.loading(t('onboarding.inviteTeam.sendingInvitations'), {
      description: t('onboarding.inviteTeam.invitingCount', { count: validInvitees.length }),
    });

    try {
      await execute(inviteOnboardingUsersMutation, {
        organizationId: organizationId!,
        invitees: validInvitees.map((inv) => ({
          firstname: inv.firstName.trim(),
          email: inv.email.trim().toLowerCase(),
        })),
        message: message.trim() || null,
      });

      analytics.capture('Onboarding Team Invited', { count: validInvitees.length });
      toast.success(t('onboarding.inviteTeam.invitationsSent'), {
        description: t('onboarding.inviteTeam.invitationsSentCount', { count: validInvitees.length }),
        id: toastId,
      });

      navigate(`/auth/onboarding/survey?organizationId=${organizationId}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('onboarding.inviteTeam.failedSend');
      toast.error(t('onboarding.inviteTeam.failedSend'), {
        description: errorMessage,
        id: toastId,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    navigate(`/auth/onboarding/survey?organizationId=${organizationId}`);
  };

  if (!organizationId) {
    navigate('/auth/onboarding/org');
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-6 w-full max-w-lg mx-auto', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='p-6 md:p-8 bg-white dark:bg-stone-900'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col items-center text-center'>
              <img src={logo} alt='Logo' className='size-16 mb-6' />
              <h1 className='text-2xl font-bold'>{t('onboarding.inviteTeam.title')}</h1>
              <p className='text-muted-foreground text-balance'>{t('onboarding.inviteTeam.description')}</p>
            </div>

            <div className='grid gap-3'>
              <Label>{t('onboarding.inviteTeam.teamMembers')}</Label>
              <div className='grid gap-2'>
                {invitees.map((invitee, index) => (
                  <div key={index} className='flex gap-2 items-start'>
                    <Input
                      placeholder={t('onboarding.inviteTeam.firstNamePlaceholder')}
                      value={invitee.firstName}
                      onChange={(e) => updateInvitee(index, 'firstName', e.target.value)}
                      disabled={isLoading}
                      className='flex-1'
                    />
                    <Input
                      type='email'
                      placeholder={t('onboarding.inviteTeam.emailPlaceholder')}
                      value={invitee.email}
                      onChange={(e) => updateInvitee(index, 'email', e.target.value)}
                      disabled={isLoading}
                      className='flex-[2]'
                    />
                    {invitees.length > 1 && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='shrink-0'
                        onClick={() => removeRow(index)}
                        disabled={isLoading}>
                        <X className='size-4' />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button type='button' variant='outline' size='sm' className='w-fit' onClick={addRow} disabled={isLoading}>
                <Plus className='size-4 mr-1' />
                {t('onboarding.inviteTeam.addAnother')}
              </Button>
            </div>

            <div className='grid gap-2'>
              <Label htmlFor='message'>{t('onboarding.inviteTeam.welcomeMessage')}</Label>
              <Textarea
                id='message'
                placeholder={t('onboarding.inviteTeam.welcomeMessagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isLoading}
                rows={3}
              />
            </div>

            <div className='flex gap-3'>
              <Button type='button' variant='outline' className='flex-1' onClick={handleSkip} disabled={isLoading}>
                {t('onboarding.inviteTeam.skip')}
              </Button>
              <Button type='button' className='flex-1' onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? t('onboarding.inviteTeam.sending') : t('onboarding.inviteTeam.sendInvites')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
